import { inject, Injectable } from '@angular/core';
import { NavController } from '@ionic/angular';
import { MenuController } from '@ionic/angular/standalone';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concat, delay, EMPTY, filter, from, map, mergeMap, of, switchMap, tap, timer, withLatestFrom } from 'rxjs';

import { SessionService } from '../../services/session.service';
import { StorageManagementPage } from '../../../pages/menu/device-settings/storage-management/storage-management.page';
import { AddCameraFlowSelectors } from '../../../pages/add-camera-flow/redux/add-camera-flow.reducer';
import { AuthenticationService } from '../../services/authentication.service';
import { BrowserService } from '../../services/browser.service';
import { DeviceService } from '../../services/device.service';
import { GeneralService } from '../../services/general.service';
import { NetworkService } from '../../services/network.service';
import { IonNavService } from '../../services/ion-nav.service';
import { SettingsService } from '../../services/settings.service';
import { ToastService } from '../../services/toast.service';

import { GeneralActions } from './general.actions';
import { OfflineBannerState } from '../../enums/offline-banner-state.enum';
import { BannerTiming } from '../../enums/offline-banner.enum';
import { GeneralSelectors } from './general.reducer';
import { Store } from '@ngrx/store';
import { createLogger } from '@hunting-nature/shared-core/logging';

@Injectable()
export class GeneralEffects {
  // TODO: keep GeneralEffects focused on global concerns; move feature navigation effects into feature domains.
  // services
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly generalService = inject(GeneralService);
  private readonly navController = inject(NavController);
  private readonly toastService = inject(ToastService);
  private readonly translocoService = inject(TranslocoService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly settingsService = inject(SettingsService);
  private readonly sessionService = inject(SessionService);
  private readonly browserService = inject(BrowserService);
  private readonly menuController = inject(MenuController);
  private readonly ionNavService = inject(IonNavService);
  private readonly networkService = inject(NetworkService);
  private readonly deviceService = inject(DeviceService);
  private readonly log = createLogger('GeneralEffects');

  // effects
  // toast go tos
  toastGoToLoginPageBecauseNotAuthorized$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.toastGoToLoginPageBecauseNotAuthorized),
        tap(() => {
          this.toastService.showNotAuthenticatedToast();
          void this.navController.navigateRoot(['/login'], { skipLocationChange: true, replaceUrl: true });
        })
      ),
    { dispatch: false }
  );

  initAppTranslation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GeneralActions.initAppTranslation),
      switchMap(() =>
        from(this.settingsService.getLanguageId$()).pipe(
          tap((language: string | undefined) => {
            if (language) {
              this.translocoService.setActiveLang(language);
            }
          }),
          map(() =>
            GeneralActions.initAppTranslationSucceeded({
              availableAppLanguages: this.generalService.getAvailableAppLanguages(),
              deviceLanguage: this.translocoService.getActiveLang(),
            })
          )
        )
      )
    )
  );

  changeAppLanguage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.changeAppLanguage),
        switchMap(({ language }) =>
          from(this.settingsService.setLanguageId$(language)).pipe(
            tap(() => {
              this.translocoService.setActiveLang(language);
            })
          )
        )
      ),
    { dispatch: false }
  );

  checkLoggedIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GeneralActions.checkLoggedIn),
      switchMap(action =>
        this.sessionService.resolveLogin$(action.url, action.isLoginCallback ?? false).pipe(
          map(result => GeneralActions.loginSucceeded(result)),
          catchError((error: unknown) => {
            this.log.error('OIDC checkLoggedIn failed', { error });
            return of(GeneralActions.loginFailed({ error }));
          })
        )
      )
    )
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GeneralActions.login),
      withLatestFrom(this.store.select(GeneralSelectors.activeAppLanguage)),
      // mergeMap so rapid taps don't cancel in-flight; catchError keeps the stream alive
      // and routes failures through the standard loginFailed path.
      // On success the browser navigates away (authorize redirect) so no action is dispatched.
      mergeMap(([action, activeAppLanguage]) =>
        this.authenticationService.logIn$(action.useDemoData, activeAppLanguage).pipe(
          mergeMap(() => EMPTY),
          catchError((error: unknown) => {
            this.log.error('Login failed', { error });
            return of(GeneralActions.loginFailed({ error }));
          })
        )
      )
    )
  );

  loginFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.loginFailed),
        // Clear the full OIDC storage cache (including authWellKnownEndPoints) so stale
        // data from a branch switch or previous session cannot loop the user back here.
        switchMap(() =>
          from(this.authenticationService.logoutLocalAll()).pipe(switchMap(() => this.navController.navigateRoot(['login'], { replaceUrl: true })))
        )
      ),
    { dispatch: false }
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GeneralActions.logout),
      switchMap(() =>
        this.authenticationService.logOut$().pipe(
          map(() => GeneralActions.logoutSucceeded()),
          catchError((error: unknown) => {
            this.log.error('Logout failed', { error });
            return of(GeneralActions.logoutFailed({ error }));
          })
        )
      )
    )
  );

  logoutSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.logoutSucceeded),
        switchMap(() => from(this.navController.navigateRoot(['/'], { replaceUrl: true })))
      ),
    { dispatch: false }
  );

  staleAuthDetected$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GeneralActions.staleAuthDetected),
      // A login round-trip completed but no callback URL arrived — stale PKCE state,
      // endpoints or a wrong redirect origin caused the loop. Nuke everything so the
      // next manual login attempt starts with a completely blank slate.
      switchMap(() =>
        from(this.authenticationService.logoutLocalAll()).pipe(map(() => GeneralActions.checkLoggedIn({ url: undefined, isLoginCallback: false })))
      )
    )
  );

  logoutFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.logoutFailed),
        tap(() => this.toastService.showTranslatedError('general.errors.logout')),
        switchMap(() => from(this.navController.navigateRoot(['/'], { replaceUrl: true })))
      ),
    { dispatch: false }
  );

  crossTabLogoutDetected$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.crossTabLogoutDetected),
        tap(() => this.authenticationService.logoutLocal()),
        switchMap(() => from(this.navController.navigateRoot(['/login'], { replaceUrl: true })))
      ),
    { dispatch: false }
  );

  /*
  appStartedAfterLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.appStartedAfterLogInOrLogout),
        switchMap(() => from(Browser.close()).pipe(catchError(() => of(void 0))))
      ),
    { dispatch: false }
  );
  */

  useDarkMode$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.useDarkMode),
        switchMap(action => this.settingsService.setUseDarkMode$(action.darkMode))
      ),
    { dispatch: false }
  );

  // go tos

  gotoHomePage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToHomePage),
        switchMap(() => this.navController.navigateBack('/'))
      ),
    { dispatch: false }
  );

  goToLoginPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToLoginPage),
        tap(() => void this.navController.navigateRoot(['/login'], { skipLocationChange: true, replaceUrl: true }))
      ),
    { dispatch: false }
  );

  // ── Navigation Effects ────────────────────────────────────────────────────
  //
  // TODO: Harden ALL navigation effects with a shared `safeNavigate` helper.
  //
  // Problem: If navController.navigateForward/Back/Root() rejects (e.g. Ionic
  // nav stack in a bad state after a page-load error), the effect's inner
  // observable errors. Without catchError the effect dies permanently —
  // all future dispatches of that action are silently ignored for the rest
  // of the session.
  //
  // Fix: Wrap every navController call with a helper that catches and logs:
  //
  //   const safeNavigate = (navFn: () => Promise<boolean>): Observable<boolean> =>
  //     from(navFn()).pipe(
  //       catchError((error: unknown) => {
  //         this.log.error('Navigation failed', { error });
  //         return of(false);
  //       })
  //     );
  //
  // Then replace all navigation effects:
  //
  //   gotoCameraDetails$ = createEffect(
  //     () => this.actions$.pipe(
  //       ofType(GeneralActions.goToCameraDetails),
  //       switchMap(action => safeNavigate(() =>
  //         this.navController.navigateForward(['home/cameras', `${action.cameraId}`])
  //       ))
  //     ),
  //     { dispatch: false }
  //   );
  //
  // This affects ~25 effects in this file — do it in a dedicated PR.
  // ──────────────────────────────────────────────────────────────────────────

  gotoCameraDetails$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToCameraDetails),
        switchMap(action => this.navController.navigateForward(['home/cameras', `${action.cameraId}`]))
      ),
    { dispatch: false }
  );

  goBackToCamerasFromCameraDetails$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goBackToCamerasFromCameraDetails),
        switchMap(_action => this.navController.navigateBack('/home/cameras'))
      ),
    { dispatch: false }
  );

  goToCameraInviteNewContact$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToCameraInviteNewContact),
        switchMap(action => this.navController.navigateForward(['home/cameras', action.cameraId, 'contacts', 'invite-new']))
      ),
    { dispatch: false }
  );

  goBackToCameraDetailsFromInviteNewContact$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goBackToCameraDetailsFromInviteNewContact),
        switchMap(action => this.navController.navigateBack(['home/cameras', action.cameraId]))
      ),
    { dispatch: false }
  );

  goToPauseCamera$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToPauseCamera),
        switchMap(action => this.navController.navigateForward(['home/cameras', action.cameraId, action.imei, action.cameraIdentCode, 'pause-camera']))
      ),
    { dispatch: false }
  );

  goBackToCameraDetailsFromPauseCamera$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goBackToCameraDetailsFromPauseCamera),
        tap(() => (globalThis.history.length > 2 ? this.navController.back() : this.navController.navigateBack('/home/cameras')))
      ),
    { dispatch: false }
  );

  goToDeleteCamera$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToDeleteCamera),
        switchMap(action => this.navController.navigateForward(['home/cameras', action.cameraId, 'delete']))
      ),
    { dispatch: false }
  );

  goToRemoteControl$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToRemoteControl),
        switchMap(action => this.navController.navigateForward(['home/cameras', action.cameraId, action.imei, action.cameraIdentCode, 'remote-control']))
      ),
    { dispatch: false }
  );

  goToRemoteControlSensitivity$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToRemoteControlSensitivity),
        switchMap(action =>
          this.navController.navigateForward(['home/cameras', action.cameraId, action.imei, action.cameraIdentCode, 'remote-control', 'sensitivity'])
        )
      ),
    { dispatch: false }
  );
  goToRemoteControlPhotoSizeSdCard$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToRemoteControlPhotoSizeSdCard),
        switchMap(action =>
          this.navController.navigateForward(['home/cameras', action.cameraId, action.imei, action.cameraIdentCode, 'remote-control', 'photo-size-sd-card'])
        )
      ),
    { dispatch: false }
  );
  goToRemoteControlImageSelection$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToRemoteControlImageSelection),
        switchMap(action =>
          this.navController.navigateForward(['home/cameras', action.cameraId, action.imei, action.cameraIdentCode, 'remote-control', 'image-selection'])
        )
      ),
    { dispatch: false }
  );
  goBackToCameraDetailsFromRemoteControl$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goBackToCameraDetailsFromRemoteControl),
        switchMap(action => this.navController.navigateBack(['home/cameras', action.cameraId]))
      ),
    { dispatch: false }
  );

  goBackToRemoteControlFromSensitivity$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goBackToRemoteControlFromSensitivity),
        switchMap(_ => this.ionNavService.navigateBack())
      ),
    { dispatch: false }
  );

  goToMenuStorageManagement$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToMenuStorageManagement),
        switchMap(() => from(this.ionNavService.pushPage(StorageManagementPage)))
      ),
    { dispatch: false }
  );

  goBackToMenuFromStorageManagement$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goBackToMenuFromStorageManagement),
        switchMap(_action => from(this.ionNavService.goBack()))
      ),
    { dispatch: false }
  );

  goToSubscriptionsAndPlans$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToSubscriptionsAndPlans),
        switchMap(() => this.navController.navigateForward(['home/subscriptions'], { replaceUrl: true }))
      ),
    { dispatch: false }
  );
  goToMenuSubscriptionsAndPlans$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToMenuSubscriptionsAndPlans),
        switchMap(async () => {
          const SubscriptionsAndPlansPage = await import('../../../pages/subscriptions-and-plans/subscriptions-and-plans.page').then(
            m => m.SubscriptionsAndPlansPage
          );
          return from(this.ionNavService.pushPage(SubscriptionsAndPlansPage));
        })
      ),
    { dispatch: false }
  );
  goBackFromSubscriptionsAndPlans$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goBackFromSubscriptionsAndPlans),
        tap(() => this.navController.back())
      ),
    { dispatch: false }
  );
  goBackToMenuFromSubscriptionsAndPlans$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goBackToMenuFromSubscriptionsAndPlans),
        switchMap(() => from(this.ionNavService.goBack()))
      ),
    { dispatch: false }
  );

  goToContacts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToContacts),
        switchMap(() => this.navController.navigateForward(['home/contacts']))
      ),
    { dispatch: false }
  );
  goToMenuContacts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToMenuContacts),
        switchMap(async () => {
          const ContactsPage = await import('../../../pages/menu-contacts/contacts.page').then(m => m.ContactsPage);
          return from(this.ionNavService.pushPage(ContactsPage));
        })
      ),
    { dispatch: false }
  );

  goBackFromContacts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goBackToCameraOverviewFromContacts),
        tap(() => this.navController.back())
      ),
    { dispatch: false }
  );

  goBackToMenuFromContacts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goBackToMenuFromContacts),
        switchMap(() => from(this.ionNavService.goBack()))
      ),
    { dispatch: false }
  );

  goToTokenTests$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToTokenTests),
        switchMap(async () => {
          const TokenTestsPage = await import('../../../pages/menu/tests/token-tests/token-tests.page').then(m => m.TokenTestsPage);
          return from(this.ionNavService.pushPage(TokenTestsPage));
        })
      ),
    { dispatch: false }
  );

  goToWebRTCTest$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToWebRTCTest),
        switchMap(async () => {
          const WebRTCTestPage = await import('../../../pages/menu/tests/webrtc-test/webrtc-test.page').then(m => m.WebrtcTestPage);
          return from(this.ionNavService.pushPage(WebRTCTestPage));
        })
      ),
    { dispatch: false }
  );

  goBackToMenuFromTokenTests$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goBackToMenuFromTokenTests),
        switchMap(_action => from(this.ionNavService.goBack()))
      ),
    { dispatch: false }
  );

  goToShotAnalysisTests$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToShotAnalysisTests),
        switchMap(() => this.menuController.close('app-menu')),
        switchMap(() => this.navController.navigateForward(['home/tests/shot-analysis']))
      ),
    { dispatch: false }
  );

  goToRemoteControlFlashMode$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToRemoteControlFlashMode),
        switchMap(action =>
          this.navController.navigateForward(['home/cameras', action.cameraId, action.imei, action.cameraIdentCode, 'remote-control', 'flash-mode'])
        )
      ),
    { dispatch: false }
  );

  goToRemoteControlMaxPhotos$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToRemoteControlMaxPhotos),
        switchMap(action =>
          this.navController.navigateForward(['home/cameras', action.cameraId, action.imei, action.cameraIdentCode, 'remote-control', 'max-photos'])
        )
      ),
    { dispatch: false }
  );

  goBackToCameraDetailsFromMaxPhotos$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goBackToRemoteControlFromMaxPhotos),
        tap(() => this.navController.back())
      ),
    { dispatch: false }
  );

  goBackToAlbumOverviewFromGalleryPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goBackToAlbumOverviewFromGalleryPage),
        tap(() => this.navController.back())
      ),
    { dispatch: false }
  );

  goToRemoteControlPauseCapture$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToRemoteControlPauseCapture),
        switchMap(action => this.navController.navigateForward(['home/cameras', action.cameraId, action.imei, action.cameraIdentCode, 'remote-control']))
      ),
    { dispatch: false }
  );

  goToGpsLocation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToCameraRemoteControlGpsLocation),
        switchMap(action =>
          this.navController.navigateForward(['home/cameras', action.cameraId, action.imei, action.cameraIdentCode, 'remote-control', 'gps-location'])
        )
      ),
    { dispatch: false }
  );

  goToShop$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.openShop),
        tap(() => this.generalService.openShop())
      ),
    { dispatch: false }
  );

  openMenu$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.openMenu),
        switchMap(() => this.menuController.open('app-menu'))
      ),
    { dispatch: false }
  );

  closeMenu$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.closeMenu),
        filter(action => !action.triggeredByGesture),
        switchMap(() => this.menuController.close('app-menu'))
      ),
    { dispatch: false }
  );

  openUserProfile$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.openUserProfile),
        switchMap(async () => {
          const url = this.translocoService.translate('menu.profile.url');
          try {
            await this.browserService.openExternalUrl(url);
          } catch (error) {
            this.log.error('Failed to open profile', { error });
          }
        })
      ),
    { dispatch: false }
  );

  goToRemoteControlMultishot$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToRemoteControlMultishot),
        switchMap(action =>
          this.navController.navigateForward(['home/cameras', action.cameraId, action.imei, action.cameraIdentCode, 'remote-control', 'multishot'])
        )
      ),
    { dispatch: false }
  );

  goToRemoteControlPhotoSize$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToRemoteControlPhotoSize),
        switchMap(action =>
          this.navController.navigateForward(['home/cameras', action.cameraId, action.imei, action.cameraIdentCode, 'remote-control', 'pic-size'])
        )
      ),
    { dispatch: false }
  );

  goBackToRemoteControlFromPhotoSize$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goBackToRemoteControlFromPhotoSize),
        tap(() => this.navController.back())
      ),
    { dispatch: false }
  );

  cancelToGoBackToRemoteControlFromPhotoSize$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.cancelAndGoBackRemoteControlFromPhotoSize),
        tap(() => this.navController.back())
      ),
    { dispatch: false }
  );

  goToRemoteControlTimeLapse$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToRemoteControlTimeLapse),
        switchMap(action =>
          this.navController.navigateForward(['home/cameras', action.cameraId, action.imei, action.cameraIdentCode, 'remote-control', 'time-lapse'])
        )
      ),
    { dispatch: false }
  );

  goBackToRemoteControlFromTimeLapse$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goBackToRemoteControlFromTimeLapse),
        tap(() => this.navController.back())
      ),
    { dispatch: false }
  );

  goToRemoteControlControlPhotos$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToRemoteControlControlPhotos),
        switchMap(action =>
          this.navController.navigateForward(['home/cameras', action.cameraId, action.imei, action.cameraIdentCode, 'remote-control', 'control-photos'])
        )
      ),
    { dispatch: false }
  );

  goBackToRemoteControlFromControlPhotos$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goBackToRemoteControlFromControlPhotos),
        tap(() => this.navController.back())
      ),
    { dispatch: false }
  );

  goToRemoteControlVideoTransmission$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToRemoteControlVideoSettings),
        switchMap(action =>
          this.navController.navigateForward(['home/cameras', action.cameraId, action.imei, action.cameraIdentCode, 'remote-control', 'video-settings'])
        )
      ),
    { dispatch: false }
  );

  goToRemoteControlStatusReport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToRemoteControlStatusReport),
        switchMap(action =>
          this.navController.navigateForward(['home/cameras', action.cameraId, action.imei, action.cameraIdentCode, 'remote-control', 'status-report'])
        )
      ),
    { dispatch: false }
  );

  goBackToRemoteControlFromStatusReport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goBackToRemoteControlFromStatusReport),
        tap(() => this.navController.back())
      ),
    { dispatch: false }
  );

  cancelAndDismissAddCameraFlow$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.cancelAndDismissAddCameraFlow),
        withLatestFrom(this.store.select(AddCameraFlowSelectors.returnUrl)),
        tap(([, returnUrl]) => {
          void this.navController.navigateRoot([returnUrl ?? '/home/cameras']);
        })
      ),
    { dispatch: false }
  );

  updateOfflineBannerOnConnectivityChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GeneralActions.offlineModeEntered, GeneralActions.offlineModeLeft),
      map(action => action.type === GeneralActions.offlineModeEntered.type),
      withLatestFrom(this.store.select(GeneralSelectors.isOfflineBannerVisible)),
      filter(([entered, visible]) => entered || visible),
      switchMap(([entered]) =>
        entered
          ? concat(
              of(GeneralActions.offlineBannerStateChanged({ state: OfflineBannerState.OfflineEntering, isVisible: true })),
              timer(BannerTiming.TRANSITION_MS).pipe(
                map(() => GeneralActions.offlineBannerStateChanged({ state: OfflineBannerState.Offline, isVisible: true }))
              )
            )
          : concat(
              of(GeneralActions.offlineBannerStateChanged({ state: OfflineBannerState.Online, isVisible: true })),
              timer(BannerTiming.TRANSITION_MS).pipe(
                map(() => GeneralActions.offlineBannerStateChanged({ state: OfflineBannerState.OnlineExiting, isVisible: false }))
              ),
              timer(BannerTiming.EXIT_ANIMATION_MS).pipe(
                map(() => GeneralActions.offlineBannerStateChanged({ state: OfflineBannerState.Hidden, isVisible: false }))
              )
            )
      )
    )
  );

  // Keep the store's offline boolean in sync with the NetworkService (connectivity SOT)
  networkConnectivity$ = createEffect(() =>
    this.networkService.status$.pipe(map(status => (status.connected ? GeneralActions.offlineModeLeft() : GeneralActions.offlineModeEntered())))
  );

  // When user requests an explicit status update from the UI, perform a manual network check.
  // `NetworkService.checkStatus$()` updates the service's `status$`, which in turn drives `GeneralActions.setOffline`.
  updateOfflineStatus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.updateOfflineStatus),
        switchMap(() =>
          from(this.networkService.checkStatus$()).pipe(
            catchError((error: unknown) => {
              this.log.error('updateOfflineStatus failed', { error });
              return of(undefined);
            })
          )
        )
      ),
    { dispatch: false }
  );

  goToSystemSetting$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToSystemSetting),
        tap(() => void this.deviceService.openAppSettings$())
      ),
    { dispatch: false }
  );

  // Ensure store `isOffline` stays in sync when the banner service emits state changes
  // (removed) Banner-driven sync: `isOffline` is driven by NetworkService.status$ only.

  goToGalleryFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GeneralActions.goToGalleryFilter),
      map(({ relatedCameraId }) => GeneralActions.goToGalleryFilterSucceeded({ relatedCameraId }))
    )
  );

  goToGalleryFilterSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToGalleryFilterSucceeded),
        delay(1), // Allow time for Ionic overlays to close based on state changes
        switchMap(({ relatedCameraId }) => {
          const path = relatedCameraId ? ['home', 'gallery', 'filter', relatedCameraId] : ['home', 'gallery', 'filter'];
          return this.navController.navigateForward(path);
        })
      ),
    { dispatch: false }
  );

  // Gallery filter pages - forward navigation
  goToGalleryFilterDetections$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToGalleryFilterDetections),
        switchMap(() => this.navController.navigateForward(['home/gallery/filter/detections']))
      ),
    { dispatch: false }
  );

  goToGalleryFilterCameras$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToGalleryFilterCameras),
        switchMap(() => this.navController.navigateForward(['home/gallery/filter/cameras']))
      ),
    { dispatch: false }
  );

  goToGalleryFilterOther$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToGalleryFilterOther),
        switchMap(() => this.navController.navigateForward(['home/gallery/filter/other']))
      ),
    { dispatch: false }
  );

  goToGalleryFilterTimeDate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToGalleryFilterTimeDate),
        switchMap(() => this.navController.navigateForward(['home/gallery/filter/timedate']))
      ),
    { dispatch: false }
  );

  goBackFromScreens$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          // Camera details
          GeneralActions.goBackToCameraDetailsFromDeleteCamera,
          GeneralActions.goBackToCameraDetailsFromGpsLocation,
          // Remote control
          GeneralActions.goBackToRemoteControlFromPhotoSizeSdCard,
          GeneralActions.goBackToRemoteControlFromImageSelection,
          GeneralActions.goBackToRemoteControlFromFlashMode,
          GeneralActions.goBackToRemoteControlFromMaxPhotos,
          GeneralActions.goBackToRemoteControlFromPauseCapture,
          GeneralActions.goBackToRemoteControlFromMultishot,
          GeneralActions.goBackToRemoteControlFromPhotoSize,
          GeneralActions.cancelAndGoBackRemoteControlFromPhotoSize,
          // Filter
          GeneralActions.goBackToGalleryFromGalleryFilter,
          GeneralActions.goBackToGalleryFilterOverviewFromDetections,
          GeneralActions.goBackToGalleryFilterOverviewFromCameras,
          GeneralActions.goBackToGalleryFilterOverviewFromOther,
          GeneralActions.goBackToGalleryFilterOverviewFromTimeDate
        ),
        tap(() => this.navController.back())
      ),
    { dispatch: false }
  );

  // BLE Device Navigation
  goBackFromDeviceDetails$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goBackFromDeviceDetails),
        tap(() => this.navController.back())
      ),
    { dispatch: false }
  );

  goToSlotManager$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToSlotManager),
        switchMap(() => this.navController.navigateForward(['home/slot-manager'], { replaceUrl: true }))
      ),
    { dispatch: false }
  );

  goBackFromSlotManager$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goBackFromSlotManager),
        tap(() => void this.navController.navigateBack('/home/cameras'))
      ),
    { dispatch: false }
  );

  goToSlotManagerFromMenu$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goToSlotManagerFromMenu),
        switchMap(async () => {
          const SlotManagerPage = await import('../../../pages/slot-manager/slot-manager.page').then(m => m.SlotManagerPage);
          return from(this.ionNavService.pushPage(SlotManagerPage));
        })
      ),
    { dispatch: false }
  );

  goBackFromSlotManagerFromMenu$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GeneralActions.goBackFromSlotManagerFromMenu),
        switchMap(() => from(this.ionNavService.goBack()))
      ),
    { dispatch: false }
  );
}
